'use client'

import React, { useState, useEffect, useId } from "react"
import AOS from "@/components/layout/AOS"
import Sidebar from "@/components/layout/Sidebar"
import { jobTitles } from "@/services/job"
import Checkbox from "@/components/form/Checkbox"
import Link from "next/link"
import { createSearchPagePathname } from "@/services/pathname"
import { getName } from "@/services/aroeven"
import Loading from "@/components/loading/PageLoading"
import { z } from 'zod'
import { messages } from '@/services/validation'
import { executeRecaptcha, useRecaptcha } from "@/services/recaptcha/client"
import { status as s } from '@/services/status'
import { createSpontaneousApplication } from "@/api/actions"

type SpontaneousApplicationProps = {
    aroeven: Aroeven,
    aroevens: Aroeven[]
}

const ApplicationSchema = z.object({
    firstName: z.string().trim().min(1, messages.required).max(255, messages.tooLong),
    lastName: z.string().trim().min(1, messages.required).max(255, messages.tooLong),
    email: z.string().trim().min(1, messages.required).email(messages.email),
    phone: z.string().trim().min(1, messages.required).min(10, messages.tooShort).max(255, messages.tooLong),
    resume: z.object({}, {
        message: messages.required
    }),
    coverLetter: z.object({}, {
        message: messages.required
    })
})

export default function SpontaneousApplication({
    aroeven,
    aroevens
}: SpontaneousApplicationProps) {

    const [application, setApplication] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        availability: '',
        resume: null,
        coverLetter: null,
        jobTitles: [],
        aroevens: aroeven.federation === true ? [] : [aroeven]
    })
    useRecaptcha()

    const [errors, setErrors] = useState([])
    const [status, setStatus] = useState(s.idle)

    const aroevenCheckboxId = useId()

    async function postApplication() {
        setErrors([])
        try {
            ApplicationSchema.parse(application)
        }
        catch (error) {
            setErrors(error.issues)
            return
        }

        setStatus(s.loading)        

        try {
            const token = await executeRecaptcha('spontaneous_application')

            const formData = new FormData()
            formData.append('coverLetter', application.coverLetter)
            formData.append('resume', application.resume)
            formData.append('firstName', application.firstName)
            formData.append('lastName', application.lastName)
            formData.append('availability', application.availability)
            formData.append('email', application.email)
            formData.append('phone', application.phone)
            console.log(application.jobTitles)
            for (const jobTitle of application.jobTitles) {
                formData.append('jobTitles', jobTitle)
            }
            for (const aroeven of application.aroevens) {
                formData.append('aroevens', aroeven['@id'])
            }
            //formData.append('jobTitles', JSON.stringify(application.jobTitles))
            //application.aroevens.map(aroeven => formData.append('aroevens', aroeven['@id']))
            //formData.append('aroevens', JSON.stringify(application.aroevens.map(aroeven => aroeven['@id'])))                    

            const sentApplication = await createSpontaneousApplication(aroeven, formData, token)
            if (!sentApplication) {
                throw new Error('Application not sent')
            }

            setStatus(s.finished)
        }
        catch (error) {
            console.log(error)
            setStatus(s.error)
        }
    }

    return (
        <AOS>
            <section className="breadcrumb py-10 d-flex items-left">
                <div className="container">
                    <div className="col-auto lh-14">
                        <i className="fi fi-rr-arrow-small-left" />
                        Retour aux <Link href={createSearchPagePathname('object', 'Job')} className="link white text-white">offres d'emploi</Link>
                    </div>
                </div>
            </section>
            <section className="heading pt-20">
                <div className="container">
                    <div className="row">
                        <div className="col-12" />
                    </div>
                </div>
                <div className="container p-10 pt-0">
                    <div className="row y-gap-30">
                        <div className="col-lg-7 col-xl-8">
                            <div className="row justify-between items-end">
                                <div className="col-auto">
                                    <h1>Rejoignez-nous !</h1>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <span className="curve" />
            </section>
            <section className="content pt-0">
                <div className="container">
                    <div className="row y-gap-30 pb-80">
                        {status === s.loading &&
                            <Loading />
                        }
                        {status === s.finished &&
                            <>
                                <div className="col-12">
                                    <h2 className="mt-20">Candidature spontanée</h2>
                                    <div className="alert alert-success" role="alert">
                                        <h4 className="alert-heading">Bien joué !</h4>
                                        <p>Votre candidature spontanée a bien été envoyée.</p>
                                    </div>
                                </div>                                
                            </>
                        }
                        {[s.idle, s.error].includes(status) === true &&
                            <>
                                <div className="col-lg-7 col-xl-8">
                                    <h2 className="mt-20">Candidature spontanée</h2>
                                    <div className="row x-gap-20 y-gap-20 pt-20">
                                        <h3>Mes informations</h3>
                                        <div className="col-md-6">
                                            <div className="form-input">
                                                <input type="text" className={errors.filter(e => e.path.join('-') === 'firstName').length > 0 ? 'requis' : ''} value={application.firstName} onChange={(e) => setApplication({
                                                    ...application,
                                                    firstName: e.target.value
                                                })} />
                                                <label className="lh-1 text-light-1">
                                                    Prénom
                                                    <span className="obligatoire">*</span>
                                                </label>
                                            </div>
                                            {errors.filter(e => e.path.join('-') === 'firstName').length > 0 && <div className="f-requis">{errors.filter(e => e.path.join('-') === 'firstName')[0].message}</div>}
                                        </div>
                                        <div className="col-md-6">
                                            <div className="form-input">
                                                <input type="text" className={errors.filter(e => e.path.join('-') === 'lastName').length > 0 ? 'requis' : ''} value={application.lastName} onChange={(e) => setApplication({
                                                    ...application,
                                                    lastName: e.target.value
                                                })} />
                                                <label className="lh-1 text-light-1">
                                                    Nom
                                                    <span className="obligatoire">*</span>
                                                </label>
                                            </div>
                                            {errors.filter(e => e.path.join('-') === 'lastName').length > 0 && <div className="f-requis">{errors.filter(e => e.path.join('-') === 'lastName')[0].message}</div>}
                                        </div>
                                        <div className="col-md-6">
                                            <div className="form-input">
                                                <input type="text" className={errors.filter(e => e.path.join('-') === 'email').length > 0 ? 'requis' : ''} value={application.email} onChange={(e) => setApplication({
                                                    ...application,
                                                    email: e.target.value
                                                })} />
                                                <label className="lh-1 text-light-1">
                                                    Adresse email
                                                    <span className="obligatoire">*</span>
                                                </label>
                                            </div>
                                            {errors.filter(e => e.path.join('-') === 'email').length > 0 && <div className="f-requis">{errors.filter(e => e.path.join('-') === 'email')[0].message}</div>}
                                        </div>
                                        <div className="col-md-6">
                                            <div className="form-input">
                                                <input type="text" className={errors.filter(e => e.path.join('-') === 'phone').length > 0 ? 'requis' : ''} value={application.phone} onChange={(e) => setApplication({
                                                    ...application,
                                                    phone: e.target.value
                                                })} />
                                                <label className="lh-1 text-light-1">
                                                    Téléphone portable
                                                    <span className="obligatoire">*</span>
                                                </label>
                                            </div>
                                            {errors.filter(e => e.path.join('-') === 'phone').length > 0 && <div className="f-requis">{errors.filter(e => e.path.join('-') === 'phone')[0].message}</div>}
                                        </div>
                                        <div className="col-12">
                                            <div className="form-input">
                                                <textarea required rows={2} value={application.availability} onChange={(e) => setApplication({
                                                    ...application,
                                                    availability: e.target.value
                                                })} />
                                                <label className="lh-1 text-light-1">Disponibilités</label>
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <label htmlFor="formFile" className="form-label m-0 p-0 text-primary-4 numbers text-18">
                                                CV
                                                <span className="obligatoire">*</span>
                                            </label>
                                            <div className="form-input pt-0">
                                                <input className={"form-control" + (errors.map(e => e.path[0]).indexOf('resume') !== -1 ? ' requis' : '')} type="file" accept={'application/pdf, application/msword, application/vnd.apple.pages, application/vnd.oasis.opendocument.text'} onChange={(e) => {
                                                    if (e.target.files.length === 0) return
                                                    setApplication({
                                                        ...application,
                                                        resume: e.target.files[0]
                                                    })
                                                }} />
                                            </div>
                                            {errors.filter(e => e.path.join('-') === 'resume').length > 0 && <div className="f-requis">{errors.filter(e => e.path.join('-') === 'resume')[0].message}</div>}
                                        </div>
                                        <div className="col-md-6">
                                            <label htmlFor="formFile" className="form-label m-0 p-0 text-primary-4 numbers text-18">
                                                Lettre de motivation
                                                <span className="obligatoire">*</span>
                                            </label>
                                            <div className="form-input pt-0">
                                                <input className={"form-control" + (errors.map(e => e.path[0]).indexOf('coverLetter') !== -1 ? ' requis' : '')} type="file" accept={'application/pdf, application/msword, application/vnd.apple.pages, application/vnd.oasis.opendocument.text'} onChange={(e) => {
                                                    if (e.target.files.length === 0) return
                                                    setApplication({
                                                        ...application,
                                                        coverLetter: e.target.files[0]
                                                    })
                                                }} />
                                            </div>
                                            {errors.filter(e => e.path.join('-') === 'coverLetter').length > 0 && <div className="f-requis">{errors.filter(e => e.path.join('-') === 'coverLetter')[0].message}</div>}
                                        </div>
                                    </div>
                                    <div className="row x-gap-20 y-gap-20 pt-40">
                                        <h3>Fonction(s) souhaitée(s)</h3>
                                        <div className="col-md-6">
                                            {Object.entries(jobTitles).slice(0, Math.ceil(Object.entries(jobTitles).length / 2)).map(([key, data], index) => {
                                                return (
                                                    <React.Fragment key={"checkbox-" + index}>
                                                        <div className="row items-center justify-between">
                                                            <div className="col-auto">
                                                                <div className="d-flex items-center">
                                                                    <Checkbox
                                                                        defaultChecked={application.jobTitles.includes(key) ?? false}
                                                                        toggle={() => setApplication({
                                                                            ...application,
                                                                            jobTitles: application.jobTitles.includes(key) ? application.jobTitles.filter(el => el !== key) : [...application.jobTitles, key]
                                                                        })}
                                                                        label={data.name}
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </React.Fragment>
                                                )
                                            })}
                                        </div>
                                        <div className="col-md-6">
                                            {Object.entries(jobTitles).slice(Math.ceil(Object.entries(jobTitles).length / 2)).map(([key, data], index) => {                                                
                                                return (
                                                    <React.Fragment key={"checkbox-" + index}>
                                                        <div className="row items-center justify-between">
                                                            <div className="col-auto">
                                                                <div className="d-flex items-center">
                                                                    <Checkbox
                                                                        defaultChecked={application.jobTitles?.includes(key) ?? false}
                                                                        toggle={() => setApplication({
                                                                            ...application,
                                                                            jobTitles: application.jobTitles.includes(key) ? application.jobTitles.filter(el => el !== key) : [...application.jobTitles, key]
                                                                        })}
                                                                        label={data.name}
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </React.Fragment>
                                                )
                                            })}
                                        </div>
                                    </div>
                                </div>
                                <div className="col-lg-5 col-xl-4 relative">
                                    <Sidebar>
                                        <div className="d-flex flex-column flex-md-row flex-lg-column justify-end" data-offset={450}>
                                            <div className="encart-2 ml-lg-0 px-30 py-30 bg-bloc w-360">
                                                <div className="row y-gap-20">
                                                    <div className="col-auto">
                                                        <div className="sidebar__item">
                                                            <h4 className="text-18 mb-20">
                                                                Transmettre ma candidature à :
                                                            </h4>
                                                            <div className="sidebar-checkbox">
                                                                <div className="row items-center justify-between">
                                                                    <div className="col-auto">
                                                                        <div className="d-flex items-center">
                                                                            <div className="checkbox-wrapper">
                                                                                <input type="checkbox" id={aroevenCheckboxId + '-all'} checked={application.aroevens.length === 0} onChange={() => setApplication({
                                                                                    ...application,
                                                                                    aroevens: []
                                                                                })} />
                                                                                <label htmlFor={aroevenCheckboxId + '-all'} className="important">
                                                                                    <span />
                                                                                    L'ensemble des Aroéven du Mouvement
                                                                                </label>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="separateur d-flex flex-row justify-content-between p-0">
                                                        <div className="divider" />
                                                        <div>ou</div>
                                                        <div className="divider" />
                                                    </div>
                                                    <div className="col-auto">
                                                        <div className="sidebar__item">
                                                            <div className="sidebar-checkbox overflow-y-scroll" style={{height: '40vh'}}>
                                                                {aroevens.filter(aroeven => aroeven.federation !== true).sort((a, b) => a.name < b.name ? -1 : 1).map((aroeven, indexAroeven) => {
                                                                    return (
                                                                        <React.Fragment key={'aroeven-' + indexAroeven}>
                                                                            <div className="row items-center justify-between">
                                                                                <div className="col-auto">
                                                                                    <div className="d-flex items-center">
                                                                                        <div className="checkbox-wrapper">
                                                                                            <input type="checkbox" id={aroevenCheckboxId + '-' + aroeven.slug} checked={application.aroevens.map(aroeven => aroeven.slug).includes(aroeven.slug)} onChange={(e) => setApplication({
                                                                                                ...application,
                                                                                                aroevens: application.aroevens.map(aroeven => aroeven.slug).includes(aroeven.slug) ? application.aroevens.filter(i => i.slug !== aroeven.slug) : [...application.aroevens, aroeven]
                                                                                            })} />
                                                                                            <label htmlFor={aroevenCheckboxId + '-' + aroeven.slug}>
                                                                                                <span />
                                                                                                {getName(aroeven)}
                                                                                            </label>
                                                                                        </div>
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        </React.Fragment>
                                                                    )
                                                                })}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="col-12">
                                                        <div className="col-12 mt-5">
                                                            <a href="#" className="button-primary demande d-block w-100 mb-10" onClick={(e) => {
                                                                e.preventDefault()
                                                                postApplication()
                                                            }}>
                                                                <i className="fi fi-bs-paper-plane mr-10" />
                                                                Envoyer
                                                            </a>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </Sidebar>
                                </div>
                            </>
                        }
                    </div>
                </div>
            </section>
        </AOS >
    )
}